import React, { useState, useEffect } from 'react';
import apiClient from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { 
  CreditCard, 
  ArrowUpRight, 
  ArrowDownRight, 
  Check, 
  Copy, 
  ExternalLink, 
  Trophy, 
  TrendingUp,
  Wallet,
  Clock,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  ShieldCheck,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Link } from 'react-router-dom';

const Settlements = () => {
  const { user, refreshUser } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Settlements states
  const [toPay, setToPay] = useState([]);
  const [toConfirm, setToConfirm] = useState([]);
  const [wonBets, setWonBets] = useState([]);
  const [history, setHistory] = useState([]);
  
  // Loading state for individual receipt confirmations
  const [confirmingPayoutId, setConfirmingPayoutId] = useState(null);

  const fetchSettlements = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const response = await apiClient.get('/payouts/overview');
      setToPay(response.data?.to_pay || []);
      setToConfirm(response.data?.to_confirm || []);
      setWonBets(response.data?.won_bets || []);
      setHistory(response.data?.history || []);
      
      // Also sync user balance
      refreshUser();
    } catch (error) {
      console.error('Failed to fetch settlements:', error);
      toast.error(language === 'de' ? 'Abrechnungsdaten konnten nicht geladen werden.' : 'Failed to load settlements data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSettlements();
  }, []);

  const handleConfirmReceipt = async (payoutId) => {
    setConfirmingPayoutId(payoutId);
    try {
      await apiClient.post('/payouts/confirm', { payout_id: payoutId });
      toast.success(language === 'de' ? 'Erhalt erfolgreich bestätigt!' : 'Receipt successfully confirmed!');
      // Refresh data
      await fetchSettlements(true);
    } catch (error) {
      console.error('Failed to confirm payout receipt:', error);
      toast.error(error.response?.data?.detail || (language === 'de' ? 'Fehler beim Bestätigen.' : 'Confirmation failed.'));
    } finally {
      setConfirmingPayoutId(null);
    }
  };

  const handleCopy = (text, message) => {
    navigator.clipboard.writeText(text);
    toast.success(message);
  };

  // Helper to open PayPal.me links safely
  const handlePaypalRedirect = (paypalLink, amount, marketTitle) => {
    if (!paypalLink) {
      toast.error(language === 'de' ? 'Kein PayPal-Link für diesen Nutzer vorhanden.' : 'No PayPal link available for this user.');
      return;
    }

    // Clean paypal.me URL
    let cleanUrl = paypalLink.trim();
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }

    // Try to append amount directly if it's a paypal.me link
    // Format: https://paypal.me/Username/10.00EUR
    try {
      const urlObj = new URL(cleanUrl);
      if (urlObj.hostname.includes('paypal.me')) {
        const pathParts = urlObj.pathname.split('/').filter(Boolean);
        if (pathParts.length > 0) {
          const username = pathParts[0];
          // Construct formatted URL with amount
          cleanUrl = `https://paypal.me/${username}/${amount.toFixed(2)}EUR`;
        }
      }
    } catch (e) {
      console.warn('Could not format paypal link with amount:', e);
    }

    window.open(cleanUrl, '_blank', 'noopener,noreferrer');
  };

  const formatCurrency = (val) => {
    return val.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }) + ' €';
  };

  const totalToPayAmount = toPay.reduce((acc, curr) => acc + (curr.amount || 0), 0);
  const totalToConfirmAmount = toConfirm.reduce((acc, curr) => acc + (curr.amount || 0), 0);

  if (loading) {
    return (
      <div className="app-container" data-testid="settlements-loading">
        <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-28 rounded-2xl" />)}
          </div>
          <Skeleton className="h-[400px] rounded-2xl" />
        </main>
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="settlements-page">
      <main className="max-w-6xl mx-auto px-4 py-4 sm:py-6 space-y-5 sm:space-y-6">
        
        {/* Header Section */}
        <div className="flex flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold flex items-center gap-2 tracking-tight">
              <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              <span>{t('settlements')}</span>
            </h1>
            <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 sm:mt-1 hidden xs:block">
              {language === 'de' 
                ? 'Verwalte deine Wettschulden, bestätige erhaltene Zahlungen und verfolge deine Wettgewinne.' 
                : 'Manage your bet debts, confirm received payments, and track your winning payouts.'}
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fetchSettlements(true)} 
            disabled={refreshing}
            className="gap-1.5 shrink-0 text-xs h-8 sm:h-9 border-border/40 hover:border-border rounded-xl transition-all"
          >
            <RefreshCw className={cn("w-3.5 h-3.5", refreshing && "animate-spin")} />
            <span className="hidden xs:inline">{language === 'de' ? 'Aktualisieren' : 'Refresh'}</span>
          </Button>
        </div>

        {/* Unified 3-Column Stats Grid */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          {/* Card 1: P&L */}
          <div className="border border-border/10 bg-gradient-to-b from-card/60 to-card/20 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.15)] hover:shadow-[0_4px_25px_rgba(0,0,0,0.25)] hover:border-border/20 transition-all duration-300 rounded-2xl p-2.5 sm:p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between gap-1">
              <span className="flex items-center gap-1.5 text-[8px] xs:text-[10px] sm:text-xs font-bold uppercase tracking-wider text-muted-foreground min-w-0">
                <span className={cn(
                  "h-1.5 w-1.5 rounded-full shrink-0",
                  (user?.balance || 0) >= 0 
                    ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]" 
                    : "bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.6)]"
                )} />
                <span className="truncate">{language === 'de' ? 'GuV (Netto)' : 'P&L (Net)'}</span>
              </span>
              <div className="hidden xs:flex p-1 bg-secondary/25 text-muted-foreground rounded-lg">
                <Wallet className="h-3.5 w-3.5" />
              </div>
            </div>
            <div className="mt-2 sm:mt-3">
              <div className={cn(
                "text-xs xs:text-sm sm:text-2xl md:text-3xl font-bold font-mono tracking-tight truncate",
                (user?.balance || 0) >= 0 ? "text-emerald-500" : "text-rose-500"
              )}>
                {(user?.balance || 0) >= 0 ? '+' : ''}{formatCurrency(user?.balance || 0)}
              </div>
              <p className="hidden sm:flex text-[9px] text-muted-foreground mt-1 items-center gap-1">
                <Check className="w-3 h-3 text-emerald-500 shrink-0" />
                <span className="truncate">{language === 'de' ? 'Bestätigte Bilanz' : 'Confirmed balance'}</span>
              </p>
            </div>
          </div>

          {/* Card 2: To Transfer */}
          <div className="border border-border/10 bg-gradient-to-b from-card/60 to-card/20 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.15)] hover:shadow-[0_4px_25px_rgba(0,0,0,0.25)] hover:border-border/20 transition-all duration-300 rounded-2xl p-2.5 sm:p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between gap-1">
              <span className="flex items-center gap-1.5 text-[8px] xs:text-[10px] sm:text-xs font-bold uppercase tracking-wider text-muted-foreground min-w-0">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.6)] shrink-0" />
                <span className="truncate">{language === 'de' ? 'Zu zahlen' : 'To Pay'}</span>
              </span>
              <div className="hidden xs:flex p-1 bg-rose-500/10 text-rose-500 rounded-lg">
                <ArrowDownRight className="h-3.5 w-3.5" />
              </div>
            </div>
            <div className="mt-2 sm:mt-3">
              <div className="text-xs xs:text-sm sm:text-2xl md:text-3xl font-bold font-mono tracking-tight text-rose-500 truncate">
                {formatCurrency(totalToPayAmount)}
              </div>
              <p className="hidden sm:flex text-[9px] text-muted-foreground mt-1 items-center gap-1">
                <AlertCircle className="w-3 h-3 text-rose-500 shrink-0" />
                <span className="truncate">{toPay.length} {language === 'de' ? 'Zahlungen offen' : 'pending payments'}</span>
              </p>
            </div>
          </div>

          {/* Card 3: Awaiting Confirmation */}
          <div className="border border-border/10 bg-gradient-to-b from-card/60 to-card/20 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.15)] hover:shadow-[0_4px_25px_rgba(0,0,0,0.25)] hover:border-border/20 transition-all duration-300 rounded-2xl p-2.5 sm:p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between gap-1">
              <span className="flex items-center gap-1.5 text-[8px] xs:text-[10px] sm:text-xs font-bold uppercase tracking-wider text-muted-foreground min-w-0">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)] shrink-0" />
                <span className="truncate">{language === 'de' ? 'Erwarten' : 'Receiving'}</span>
              </span>
              <div className="hidden xs:flex p-1 bg-amber-500/10 text-amber-500 rounded-lg">
                <ArrowUpRight className="h-3.5 w-3.5" />
              </div>
            </div>
            <div className="mt-2 sm:mt-3">
              <div className="text-xs xs:text-sm sm:text-2xl md:text-3xl font-bold font-mono tracking-tight text-amber-500 truncate">
                {formatCurrency(totalToConfirmAmount)}
              </div>
              <p className="hidden sm:flex text-[9px] text-muted-foreground mt-1 items-center gap-1">
                <Clock className="w-3 h-3 text-amber-500 shrink-0" />
                <span className="truncate">{toConfirm.length} {language === 'de' ? 'Bestätigungen' : 'confirmations'}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Main Tabs Layout */}
        <Tabs defaultValue="to_pay" className="w-full">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-1 w-full h-auto bg-secondary/10 border border-border/10 backdrop-blur-md p-1 rounded-xl">
            <TabsTrigger value="to_pay" className="rounded-lg text-xs sm:text-sm flex gap-1 items-center justify-center w-full h-auto py-2 px-2 sm:px-3 data-[state=active]:bg-background/80 data-[state=active]:backdrop-blur-sm transition-all duration-300">
              <span>{t('toPay')}</span>
              {toPay.length > 0 && (
                <span className="ml-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white px-1 leading-none shadow-[0_0_10px_rgba(244,63,94,0.4)]">
                  {toPay.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="to_confirm" className="rounded-lg text-xs sm:text-sm flex gap-1 items-center justify-center w-full h-auto py-2 px-2 sm:px-3 data-[state=active]:bg-background/80 data-[state=active]:backdrop-blur-sm transition-all duration-300">
              <span>{t('toConfirm')}</span>
              {toConfirm.length > 0 && (
                <span className="ml-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-black px-1 leading-none shadow-[0_0_10px_rgba(245,158,11,0.4)]">
                  {toConfirm.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="won_bets" className="rounded-lg text-xs sm:text-sm flex items-center justify-center w-full h-auto py-2 px-2 sm:px-3 data-[state=active]:bg-background/80 data-[state=active]:backdrop-blur-sm transition-all duration-300">
              {t('wonBetsOverview')}
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-lg text-xs sm:text-sm flex items-center justify-center w-full h-auto py-2 px-2 sm:px-3 data-[state=active]:bg-background/80 data-[state=active]:backdrop-blur-sm transition-all duration-300">
              {t('settlementHistory')}
            </TabsTrigger>
          </TabsList>

          {/* TAB: To Pay */}
          <TabsContent value="to_pay" className="mt-4 sm:mt-6 focus-visible:outline-none">
            {toPay.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {toPay.map(item => {
                  const displayName = item.payee_nickname || item.payee_username;
                  return (
                    <div 
                      key={item.id} 
                      className="border border-border/10 hover:border-rose-500/30 bg-gradient-to-b from-card/50 to-card/10 backdrop-blur-lg hover:shadow-[0_8px_30px_rgba(244,63,94,0.05)] transition-all duration-300 rounded-2xl overflow-hidden flex flex-col justify-between animate-fade-in"
                    >
                      <div className="p-4 sm:p-5 space-y-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 border border-border/40 hover:border-rose-500/30 transition-all duration-300 shadow-inner">
                              <AvatarImage src={item.payee_avatar_url} alt={item.payee_username} className="object-cover" />
                              <AvatarFallback className="bg-rose-500/10 text-rose-500 font-bold">
                                {item.payee_username?.[0]?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-bold text-sm sm:text-base flex items-center gap-1.5 tracking-tight text-foreground">
                                {displayName}
                                {item.payee_nickname && (
                                  <span className="text-[10px] text-muted-foreground font-normal">(@{item.payee_username})</span>
                                )}
                              </p>
                              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5 text-muted-foreground/40" />
                                {new Date(item.created_at).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg sm:text-xl font-bold font-mono text-rose-400 tracking-tight">
                              {formatCurrency(item.amount)}
                            </p>
                          </div>
                        </div>

                        {/* Receipt-like items */}
                        <div className="border-t border-border/10 pt-3.5 space-y-2">
                          <div className="flex justify-between items-start gap-4 text-xs">
                            <span className="text-muted-foreground shrink-0">{language === 'de' ? 'Verwendungszweck' : 'Reference'}:</span>
                            <span className="font-medium text-foreground text-right break-words line-clamp-2">{item.market_title}</span>
                          </div>
                          {item.payee_paypal_link && (
                            <div className="flex justify-between items-center gap-4 text-xs">
                              <span className="text-muted-foreground shrink-0">PayPal Link:</span>
                              <span className="font-mono text-[10px] text-muted-foreground truncate max-w-[150px] sm:max-w-[200px]">{item.payee_paypal_link}</span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-2 border-t border-border/5">
                          <Button 
                            className="flex-1 bg-[#0070ba] hover:bg-[#005ea6] text-white font-bold gap-2 text-xs h-9.5 shadow-md shadow-[#0070ba]/10 hover:shadow-[#0070ba]/20 transition-all duration-300 rounded-xl"
                            onClick={() => handlePaypalRedirect(item.payee_paypal_link, item.amount, item.market_title)}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            {t('openPaypal')}
                          </Button>
                          
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="shrink-0 gap-1.5 text-xs h-9.5 border-border/40 hover:border-border/80 rounded-xl transition-all duration-300"
                            onClick={() => handleCopy(
                              `${item.amount.toFixed(2)} EUR - Scherenmarket: ${item.market_title}`,
                              language === 'de' ? 'Auszahlungs-Info kopiert!' : 'Payout info copied!'
                            )}
                          >
                            <Copy className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{t('copyInfo')}</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Card className="p-12 text-center border-border/10 bg-secondary/5 rounded-2xl">
                <div className="max-w-sm mx-auto flex flex-col items-center">
                  <div className="p-4 rounded-full bg-green-500/10 text-green-500 mb-4 shadow-[0_0_15px_rgba(34,197,94,0.1)]">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold">{language === 'de' ? 'Schuldenfrei!' : 'Debt Free!'}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('noPayoutsToPay')}
                  </p>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* TAB: To Confirm Receipt */}
          <TabsContent value="to_confirm" className="mt-4 sm:mt-6 focus-visible:outline-none">
            {toConfirm.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {toConfirm.map(item => {
                  const displayName = item.payer_nickname || item.payer_username;
                  const isConfirming = confirmingPayoutId === item.id;
                  return (
                    <div 
                      key={item.id} 
                      className="border border-border/10 hover:border-emerald-500/30 bg-gradient-to-b from-card/50 to-card/10 backdrop-blur-lg hover:shadow-[0_8px_30px_rgba(16,185,129,0.05)] transition-all duration-300 rounded-2xl overflow-hidden flex flex-col justify-between animate-fade-in"
                    >
                      <div className="p-4 sm:p-5 space-y-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 border border-border/40 hover:border-emerald-500/30 transition-all duration-300 shadow-inner">
                              <AvatarImage src={item.payer_avatar_url} alt={item.payer_username} className="object-cover" />
                              <AvatarFallback className="bg-emerald-500/10 text-emerald-500 font-bold">
                                {item.payer_username?.[0]?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-bold text-sm sm:text-base flex items-center gap-1.5 tracking-tight text-foreground">
                                {displayName}
                                {item.payer_nickname && (
                                  <span className="text-[10px] text-muted-foreground font-normal">(@{item.payer_username})</span>
                                )}
                              </p>
                              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5 text-muted-foreground/40" />
                                {new Date(item.created_at).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg sm:text-xl font-bold font-mono text-emerald-400 tracking-tight">
                              {formatCurrency(item.amount)}
                            </p>
                          </div>
                        </div>

                        {/* Receipt-like details */}
                        <div className="border-t border-border/10 pt-3.5 space-y-2">
                          <div className="flex justify-between items-start gap-4 text-xs">
                            <span className="text-muted-foreground shrink-0">{language === 'de' ? 'Wette' : 'Bet'}:</span>
                            <span className="font-medium text-foreground text-right break-words line-clamp-2">{item.market_title}</span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="pt-2 border-t border-border/5">
                          <Button 
                            className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold gap-2 text-xs h-9.5 shadow-md shadow-emerald-500/10 hover:shadow-emerald-500/20 transition-all duration-300 transform active:scale-[0.98] rounded-xl"
                            disabled={isConfirming || confirmingPayoutId !== null}
                            onClick={() => handleConfirmReceipt(item.id)}
                          >
                            {isConfirming ? (
                              <RefreshCw className="w-3.5 h-3.5 animate-spin text-black" />
                            ) : (
                              <Check className="w-3.5 h-3.5 text-black" />
                            )}
                            {t('confirmReceipt')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Card className="p-12 text-center border-border/10 bg-secondary/5 rounded-2xl">
                <div className="max-w-sm mx-auto flex flex-col items-center">
                  <div className="p-4 rounded-full bg-secondary/30 text-muted-foreground mb-4">
                    <Check className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold">{language === 'de' ? 'Alles quittiert' : 'All Settled'}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('noPayoutsToConfirm')}
                  </p>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* TAB: Won Bets */}
          <TabsContent value="won_bets" className="mt-4 sm:mt-6 focus-visible:outline-none">
            {wonBets.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {wonBets.map(bet => {
                  const confirmed = parseFloat(bet.confirmed_winnings || 0);
                  const pending = parseFloat(bet.pending_winnings || 0);
                  const totalWinnings = confirmed + pending;
                  const originalStake = parseFloat(bet.amount || 0);
                  const totalPossiblePayout = originalStake + totalWinnings;
                  
                  // Compute confirmation progress
                  const totalPayoutCollected = originalStake + confirmed;
                  const progressPercentage = totalPossiblePayout > 0 
                    ? (totalPayoutCollected / totalPossiblePayout) * 100 
                    : 100;

                  return (
                    <div 
                      key={bet.id} 
                      className="border border-border/10 hover:border-amber-500/30 bg-gradient-to-b from-card/50 to-card/10 backdrop-blur-lg hover:shadow-[0_8px_30px_rgba(245,158,11,0.05)] transition-all duration-300 rounded-2xl overflow-hidden flex flex-col justify-between animate-fade-in"
                    >
                      <div className="p-4 sm:p-5 space-y-4">
                        <div className="flex justify-between items-center gap-4">
                          <Badge className="bg-emerald-500/15 text-emerald-500 border border-emerald-500/20 font-bold px-2 py-0.5 text-[9px] rounded-lg tracking-wider">
                            {language === 'de' ? 'GEWONNEN' : 'WON'}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-muted-foreground/40" />
                            {new Date(bet.created_at).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </span>
                        </div>

                        <h3 className="text-sm sm:text-base font-bold line-clamp-2 tracking-tight">
                          <Link to={`/market/${bet.market_id}`} className="hover:underline hover:text-primary transition-all">
                            {bet.market_title}
                          </Link>
                        </h3>

                        <div className="grid grid-cols-3 gap-2 p-3 bg-secondary/10 border border-border/10 rounded-xl text-center">
                          <div>
                            <p className="text-[8px] text-muted-foreground font-semibold uppercase tracking-wider">
                              {language === 'de' ? 'Einsatz' : 'Stake'}
                            </p>
                            <p className="text-xs sm:text-sm font-bold font-mono mt-0.5 text-foreground">
                              {formatCurrency(originalStake)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[8px] text-muted-foreground font-semibold uppercase tracking-wider">
                              {language === 'de' ? 'Reingewinn' : 'Profit'}
                            </p>
                            <p className="text-xs sm:text-sm font-bold font-mono text-emerald-400 mt-0.5">
                              +{formatCurrency(totalWinnings)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[8px] text-muted-foreground font-semibold uppercase tracking-wider">
                              {language === 'de' ? 'Auszahlung' : 'Total'}
                            </p>
                            <p className="text-xs sm:text-sm font-bold font-mono text-primary mt-0.5">
                              {formatCurrency(totalPossiblePayout)}
                            </p>
                          </div>
                        </div>

                        {/* Progress Bar of Settlement */}
                        <div className="space-y-1.5 border-t border-border/10 pt-3.5">
                          <div className="flex justify-between items-center text-[10px] sm:text-xs font-semibold">
                            <span className="text-muted-foreground flex items-center gap-1.5">
                              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                              {language === 'de' ? 'Erhalten' : 'Payout'}
                            </span>
                            <span className={cn(progressPercentage === 100 ? "text-emerald-400 font-bold" : "text-amber-400 font-bold font-mono")}>
                              {formatCurrency(totalPayoutCollected)} / {formatCurrency(totalPossiblePayout)}
                            </span>
                          </div>
                          <div className="w-full bg-secondary/30 h-1.5 rounded-full overflow-hidden border border-border/5 relative">
                            <div 
                              className={cn(
                                "h-full transition-all duration-500 rounded-full",
                                progressPercentage === 100 
                                  ? "bg-gradient-to-r from-emerald-500 to-green-400 shadow-[0_0_8px_rgba(16,185,129,0.3)]" 
                                  : "bg-gradient-to-r from-amber-500 to-yellow-400 shadow-[0_0_8px_rgba(245,158,11,0.2)]"
                              )} 
                              style={{ width: `${progressPercentage}%` }} 
                            />
                          </div>
                          <div className="flex justify-between items-center text-[9px] text-muted-foreground">
                            <span className="font-medium">{progressPercentage.toFixed(0)}% {language === 'de' ? 'eingefordert' : 'collected'}</span>
                            {pending > 0 && (
                              <span className="text-amber-400 font-semibold flex items-center gap-1 animate-pulse">
                                <AlertCircle className="w-3 h-3 shrink-0" />
                                {formatCurrency(pending)} {language === 'de' ? 'ausstehend' : 'pending'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Card className="p-12 text-center border-border/10 bg-secondary/5 rounded-2xl">
                <div className="max-w-sm mx-auto flex flex-col items-center">
                  <div className="p-4 rounded-full bg-amber-500/10 text-amber-500 mb-4">
                    <Trophy className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold">{language === 'de' ? 'Noch kein Sieg' : 'No Wins Yet'}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('noWonBetsYet')}
                  </p>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* TAB: History */}
          <TabsContent value="history" className="mt-4 sm:mt-6 focus-visible:outline-none">
            {history.length > 0 ? (
              <div className="space-y-2">
                {history.map(item => {
                  const isUserPayee = item.payee_id === user?.id;
                  const counterpartName = isUserPayee
                    ? (item.payer_nickname || item.payer_username)
                    : (item.payee_nickname || item.payee_username);
                  
                  return (
                    <div 
                      key={item.id} 
                      className="p-3.5 sm:p-4 rounded-xl border border-border/10 bg-card/20 hover:bg-card/40 hover:border-border/30 backdrop-blur-sm flex items-center justify-between gap-3 transition-all duration-200"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={cn(
                          "p-2 rounded-xl border shrink-0",
                          isUserPayee 
                            ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.05)]" 
                            : "bg-rose-500/10 text-rose-400 border-rose-500/20 shadow-[0_0_10px_rgba(244,63,94,0.05)]"
                        )}>
                          {isUserPayee ? (
                            <ArrowUpRight className="w-4 h-4" />
                          ) : (
                            <ArrowDownRight className="w-4 h-4" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm font-bold truncate text-foreground">
                            {item.market_title}
                          </p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
                            {isUserPayee 
                              ? `${language === 'de' ? 'Erhalten von' : 'Received from'} @${counterpartName}`
                              : `${language === 'de' ? 'Gezahlt an' : 'Paid to'} @${counterpartName}`
                            }
                          </p>
                        </div>
                      </div>

                      <div className="text-right shrink-0 flex flex-col items-end gap-1">
                        <span className={cn(
                          "font-mono text-xs sm:text-sm font-bold",
                          isUserPayee ? "text-emerald-400" : "text-rose-400"
                        )}>
                          {isUserPayee ? '+' : '-'}{formatCurrency(item.amount)}
                        </span>
                        <span className="text-[9px] text-muted-foreground flex items-center gap-1">
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          {new Date(item.created_at).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Card className="p-12 text-center border-border/10 bg-secondary/5 rounded-2xl">
                <div className="max-w-sm mx-auto flex flex-col items-center">
                  <div className="p-4 rounded-full bg-secondary/30 text-muted-foreground mb-4">
                    <Clock className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold">{language === 'de' ? 'Kein Verlauf' : 'No History'}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('noPayoutHistory')}
                  </p>
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Settlements;
